import { Checkbox } from "primereact/checkbox";
import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import styled from "styled-components";

import { plural, ruleCountLabel } from "../helpers/ruleBucket";
import { useCodeListFamily, useRuleBucket } from "../hooks/useRuleBucket";

const NameHint = styled.small`
  display: block;
  margin-top: 0.5rem;
  color: #6b7280;
`;

const FamilyBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const MAX_LISTED_NAMES = 3;

const listNames = (names: string[]) => {
  const listed = names.slice(0, MAX_LISTED_NAMES).join(", ");
  return names.length > MAX_LISTED_NAMES ? `${listed}, …` : listed;
};

/**
 * CLE-2842 Part 3 — the name is not part of the rule: a bucket's members may carry different names
 * ("T", "T codes", "Hospital Outpatient: T codes"). In a group scope the Rule Name field shows the
 * display name of the members in scope, and a save renames them only when the user EDITS it, so:
 *   - names unchanged: a neutral note that some members use other names;
 *   - name edited: which members the save renames.
 * Switching the scope puts the matching name back in the field: the scope's display name, or this
 * rule's own name for "Only this payer".
 */
export function RuleBucketNameHint() {
  const bucket = useRuleBucket();
  const { control, setValue } = useFormContext<any>();
  const groupName = useWatch({ control, name: "groupName" });
  const scope = bucket?.scope;
  const ownName = bucket?.ownName ?? "";
  const scopeDisplayName = bucket?.scopeDisplayName ?? "";
  const shownScope = useRef(scope);
  useEffect(() => {
    if (!scope || shownScope.current === scope) return;
    shownScope.current = scope;
    setValue("groupName", scope === "PAYER" ? ownName : scopeDisplayName);
  }, [scope, ownName, scopeDisplayName, setValue]);

  if (!bucket?.isGroupScope) return null;
  const name = String(groupName ?? "").trim();
  const edited = name !== scopeDisplayName.trim();
  const nameOf = (member: { groupName: string | null }) =>
    (member.groupName ?? "").trim();
  const differing = bucket.activeMembers.filter(
    (member) => nameOf(member) !== (edited ? name : scopeDisplayName.trim())
  );
  if (!differing.length) return null;
  const names = Array.from(
    new Set(differing.map((member) => nameOf(member) || "(no name)"))
  );
  const one = differing.length === 1;
  return (
    <NameHint data-testid="bucket-name-hint">
      {edited
        ? `${ruleCountLabel(differing.length)} in this scope ${
            one ? "is" : "are"
          } named differently (${listNames(names)}). Saving renames ${
            one ? "it" : "them"
          } to the name above.`
        : `${ruleCountLabel(differing.length)} in this scope ${
            one ? "uses another name" : "use other names"
          } (${listNames(names)}). Edit the name to rename them all.`}
    </NameHint>
  );
}

/**
 * "Also update this code list on N other rules that use the same list." — with "(K with different
 * discounts)" when some of them use it with other terms.
 */
export const familyLabel = (
  familyCount: number,
  otherRuleCount: number,
  termsWord: string
) => {
  const differing =
    otherRuleCount > 0
      ? ` (${otherRuleCount} with different ${termsWord})`
      : "";
  return `Also update this code list on ${plural(
    familyCount,
    "other rule"
  )} that use${familyCount === 1 ? "s" : ""} the same list${differing}.`;
};

/**
 * CLE-2842 Part 3 — the code-list family checkbox: shown when a group save changes the code lists
 * and other rules use the same list (bucket members outside the scope, or rules with other terms).
 * Ticked (the default), the save also rewrites those rules' code lists — their names, terms and
 * contract language stay.
 */
export function CodeListFamilyCheckbox({
  termsWord = "discounts",
}: {
  /** What the other rules differ in: "discounts" for MPPR, "terms" for Case Rate / Package. */
  termsWord?: string;
}) {
  const family = useCodeListFamily();
  if (!family.eligible) return null;
  return (
    <FamilyBox>
      <Checkbox
        inputId="alsoUpdateCodeListFamily"
        checked={family.checked}
        disabled={family.saving}
        onChange={(e) => family.setChecked(!!e.checked)}
      />
      <label htmlFor="alsoUpdateCodeListFamily" style={{ cursor: "pointer" }}>
        {familyLabel(family.familyCount, family.otherRuleCount, termsWord)}
      </label>
    </FamilyBox>
  );
}
